import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, AlertCircle, CheckCircle2, ArrowRight, Info, RefreshCw } from 'lucide-react';
import { domainApi, gscApi, type DomainInstructions } from '@/lib/api';
import { cn, formatNumber } from '@/lib/utils';
import { CnameInstructions } from './CnameInstructions';
import { GscStep } from './GscStep';
import { useAuth } from '@/contexts/AuthContext';

type WizardStep = 'input' | 'budget' | 'instructions' | 'verify' | 'gsc';

interface WizardState {
  step: WizardStep;
  subdomain: string;
  urlBudget: number;
  domainId: string | null;
  instructions: DomainInstructions | null;
  error: string | null;
  isLoading: boolean;
  verifyStatus: 'idle' | 'checking' | 'success' | 'failed';
  verifyMessage: string | null;
  verifyAttempts: number;
  autoVerifyEnabled: boolean;
}

const AUTO_VERIFY_INTERVAL = 30000; // 30 seconds
const MAX_AUTO_VERIFY_ATTEMPTS = 20; // Stop after ~10 minutes

// --- Sitemap host validation -------------------------------------------------
// Customers CNAME a dedicated subdomain (sitemap.mysite.com) at us. The two
// mistakes we see in the wild are entering a bare apex (mysite.com) — which
// can't take a CNAME — or a www host, which is the main site. Both sail
// through to CNAME instructions that can never verify, so catch them here.
//
// To tell an apex from a real subdomain we need the registrable domain, which
// depends on the public suffix. This is a pragmatic list of common multi-part
// suffixes — a miss degrades to "allowed" (same as today), never to a false
// block of a legitimate subdomain.
const MULTI_PART_TLDS = new Set([
  'co.uk', 'org.uk', 'me.uk', 'ac.uk', 'gov.uk', 'net.uk', 'sch.uk',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au',
  'co.nz', 'net.nz', 'org.nz',
  'com.br', 'net.br', 'org.br',
  'co.za', 'org.za',
  'co.ke', 'or.ke', 'go.ke', 'ac.ke',
  'co.in', 'net.in', 'org.in', 'gen.in', 'firm.in',
  'com.mx', 'org.mx',
  'co.jp', 'or.jp', 'ne.jp', 'go.jp',
  'com.sg', 'com.hk', 'com.tw', 'com.tr', 'com.ar', 'com.co',
  'co.id', 'co.th', 'com.ph', 'com.my', 'com.ua', 'com.pl',
]);

const HOSTNAME_LABEL = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

type HostCheck =
  | { ok: true; value: string }
  | { ok: false; error: string; suggestion?: string };

export function validateSitemapHost(raw: string): HostCheck {
  let host = raw.trim().toLowerCase();
  // Tolerate a pasted URL: strip scheme, path/query/hash, and trailing dot.
  host = host.replace(/^[a-z]+:\/\//, '').replace(/[/?#].*$/, '').replace(/\.$/, '').trim();

  if (!host) {
    return { ok: false, error: 'Enter the subdomain where you want to host your sitemap.' };
  }

  const labels = host.split('.');
  if (labels.length < 2 || labels.some((l) => l === '')) {
    return {
      ok: false,
      error: 'Enter the full subdomain including your domain, e.g. sitemap.yoursite.com',
    };
  }
  if (!labels.every((l) => HOSTNAME_LABEL.test(l))) {
    return {
      ok: false,
      error: 'That doesn’t look like a valid hostname. Use letters, numbers and hyphens, e.g. sitemap.yoursite.com',
    };
  }

  // Registrable (apex) domain = last 2 labels, or last 3 for a known multi-part TLD.
  const lastTwo = labels.slice(-2).join('.');
  const registrableLen = MULTI_PART_TLDS.has(lastTwo) ? 3 : 2;
  const registrable = labels.slice(-registrableLen).join('.');

  // Bare apex — no subdomain label. Can't take a CNAME.
  if (host === registrable) {
    return {
      ok: false,
      error: `“${host}” is your root domain, which can’t point a CNAME at us. Host the sitemap on a subdomain.`,
      suggestion: `sitemap.${host}`,
    };
  }

  // www is the main website, not a sitemap host.
  if (labels[0] === 'www') {
    return {
      ok: false,
      error: '“www” is your main website. Use a dedicated subdomain like “sitemap.” instead.',
      suggestion: `sitemap.${labels.slice(1).join('.')}`,
    };
  }

  return { ok: true, value: host };
}

export function DomainWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refresh: refreshAuth, pool, limits } = useAuth();

  // Calculate available budget for new domain
  const availableBudget = pool ? (pool.available === -1 ? 1000000 : pool.available) : 0;
  const minBudget = limits?.min_urls_per_domain || 100;
  const defaultBudget = Math.min(Math.max(minBudget, 10000), availableBudget);

  const [state, setState] = useState<WizardState>({
    step: 'input',
    subdomain: '',
    urlBudget: defaultBudget,
    domainId: null,
    instructions: null,
    error: null,
    isLoading: false,
    verifyStatus: 'idle',
    verifyMessage: null,
    verifyAttempts: 0,
    autoVerifyEnabled: true,
  });

  const autoVerifyTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-verify function
  const runVerification = useCallback(async (isManual = false) => {
    if (!state.domainId) return;

    setState((s) => ({
      ...s,
      verifyStatus: 'checking',
      verifyMessage: null,
      verifyAttempts: isManual ? s.verifyAttempts : s.verifyAttempts + 1,
    }));

    try {
      const result = await domainApi.verify(state.domainId);
      setState((s) => ({
        ...s,
        verifyStatus: result.verified ? 'success' : 'failed',
        verifyMessage: result.message,
      }));

      if (result.verified) {
        // Clear auto-verify timer
        if (autoVerifyTimerRef.current) {
          clearInterval(autoVerifyTimerRef.current);
          autoVerifyTimerRef.current = null;
        }
        // Invalidate domains cache so dashboard shows updated status
        queryClient.invalidateQueries({ queryKey: ['domains'] });
        refreshAuth();
        // Move to GSC step after short delay
        setTimeout(() => {
          setState((s) => ({ ...s, step: 'gsc' }));
        }, 1500);
      }
    } catch (error) {
      setState((s) => ({
        ...s,
        verifyStatus: 'failed',
        verifyMessage: error instanceof Error ? error.message : 'Verification failed',
      }));
    }
  }, [state.domainId, navigate]);

  // Auto-verify effect when on verify step
  useEffect(() => {
    // Start auto-verify when entering verify step
    if (state.step === 'verify' && state.autoVerifyEnabled && state.domainId && state.verifyStatus !== 'success') {
      // Run first verification immediately
      if (state.verifyAttempts === 0) {
        runVerification();
      }

      // Set up interval for subsequent checks
      autoVerifyTimerRef.current = setInterval(() => {
        setState((s) => {
          // Stop if max attempts reached or verified
          if (s.verifyAttempts >= MAX_AUTO_VERIFY_ATTEMPTS || s.verifyStatus === 'success') {
            if (autoVerifyTimerRef.current) {
              clearInterval(autoVerifyTimerRef.current);
              autoVerifyTimerRef.current = null;
            }
            return { ...s, autoVerifyEnabled: false };
          }
          return s;
        });

        // Only run if still enabled and not currently checking
        if (state.verifyStatus !== 'checking' && state.verifyStatus !== 'success') {
          runVerification();
        }
      }, AUTO_VERIFY_INTERVAL);

      return () => {
        if (autoVerifyTimerRef.current) {
          clearInterval(autoVerifyTimerRef.current);
          autoVerifyTimerRef.current = null;
        }
      };
    }
  }, [state.step, state.autoVerifyEnabled, state.domainId, state.verifyAttempts, state.verifyStatus, runVerification]);

  const handleSubdomainSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const check = validateSitemapHost(state.subdomain);
    if (!check.ok) {
      // On a fixable mistake (apex / www) pre-fill the corrected host so one
      // more click on Continue proceeds; otherwise just show why it's invalid.
      setState((s) => ({
        ...s,
        subdomain: check.suggestion ?? s.subdomain,
        error: check.suggestion
          ? `${check.error} We’ve set it to “${check.suggestion}” — press Continue to use it, or edit.`
          : check.error,
      }));
      return;
    }

    // Move to budget allocation step with the normalized host.
    setState((s) => ({ ...s, subdomain: check.value, step: 'budget', error: null }));
  };

  const handleBudgetSubmit = async () => {
    setState((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const result = await domainApi.create(state.subdomain, state.urlBudget);

      // Refresh data so dashboard shows the new domain
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      refreshAuth();

      setState((s) => ({
        ...s,
        step: 'instructions',
        domainId: result.domain.id,
        instructions: result.instructions,
        isLoading: false,
      }));
    } catch (error) {
      setState((s) => ({
        ...s,
        error: error instanceof Error ? error.message : 'Failed to create domain',
        isLoading: false,
      }));
    }
  };

  const handleVerify = () => {
    runVerification(true);
  };

  const toggleAutoVerify = () => {
    setState((s) => ({ ...s, autoVerifyEnabled: !s.autoVerifyEnabled, verifyAttempts: 0 }));
  };

  const steps = [
    { key: 'input', label: 'Enter Domain' },
    { key: 'budget', label: 'URL Budget' },
    { key: 'instructions', label: 'Configure DNS' },
    { key: 'verify', label: 'Verify' },
    { key: 'gsc', label: 'GSC' },
  ];

  const currentStepIndex = steps.findIndex((s) => s.key === state.step);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step indicator */}
      <div className="mb-8">
        <div className="relative flex items-start justify-between">
          {/* Connector line background */}
          <div className="absolute top-5 left-0 right-0 flex px-10">
            {steps.slice(0, -1).map((step, index) => (
              <div
                key={`connector-${step.key}`}
                className={cn(
                  'flex-1 h-0.5',
                  index < currentStepIndex ? 'bg-primary' : 'bg-slate-200'
                )}
              />
            ))}
          </div>
          {/* Steps */}
          {steps.map((step, index) => (
            <div key={step.key} className="flex flex-col items-center z-10">
              <div
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-full border-2 font-medium bg-white',
                  index < currentStepIndex
                    ? 'bg-primary border-primary text-white'
                    : index === currentStepIndex
                      ? 'border-primary text-primary'
                      : 'border-slate-300 text-slate-400'
                )}
              >
                {index < currentStepIndex ? <Check className="w-5 h-5" /> : index + 1}
              </div>
              <span className="text-xs text-slate-500 mt-2 text-center whitespace-nowrap">
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Step content */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        {/* Step 1: Input subdomain */}
        {state.step === 'input' && (
          <div>
            <h2 className="text-xl font-semibold mb-2">Add Your Domain</h2>
            <p className="text-slate-600 mb-6">
              Enter the subdomain where you want to host your sitemap. This is typically something
              like <code className="text-sm bg-slate-100 px-1 py-0.5 rounded">sitemap.yoursite.com</code>
            </p>

            <form onSubmit={handleSubdomainSubmit}>
              <div className="mb-4">
                <label htmlFor="subdomain" className="block text-sm font-medium text-slate-700 mb-1">
                  Subdomain
                </label>
                <input
                  id="subdomain"
                  type="text"
                  value={state.subdomain}
                  onChange={(e) => setState((s) => ({ ...s, subdomain: e.target.value, error: null }))}
                  placeholder="sitemap.example.com"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the full subdomain, e.g., sitemap.yoursite.com
                </p>
              </div>

              {state.error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {state.error}
                </div>
              )}

              <button
                type="submit"
                disabled={state.isLoading || !state.subdomain.trim()}
                className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {state.isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Budget Allocation */}
        {state.step === 'budget' && (
          <div>
            <h2 className="text-xl font-semibold mb-2">Allocate URL Budget</h2>
            <p className="text-slate-600 mb-6">
              Choose how many URLs from your pool to allocate to{' '}
              <code className="text-sm bg-slate-100 px-1 py-0.5 rounded">{state.subdomain}</code>
            </p>

            {/* Pool Status */}
            <div className="p-4 bg-slate-50 rounded-lg mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-slate-500">Available in Pool</span>
                <span className="font-semibold">
                  {pool?.available === -1 ? 'Unlimited' : formatNumber(availableBudget)}
                </span>
              </div>
              {pool && pool.available !== -1 && (
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${Math.min((pool.allocated / pool.url_pool) * 100, 100)}%` }}
                  />
                </div>
              )}
            </div>

            {/* No budget available warning */}
            {availableBudget === 0 && pool && pool.domains && pool.domains.length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-2">No URLs available in pool</p>
                    <p className="mb-3">
                      Your existing domains have allocated the entire URL pool. To add a new domain,
                      reduce the budget of an existing domain first:
                    </p>
                    <ul className="space-y-1">
                      {pool.domains.map((d) => (
                        <li key={d.id} className="flex justify-between">
                          <span>{d.domain}</span>
                          <span className="font-mono">{formatNumber(d.url_budget)} URLs</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href={`/domains/${pool.domains[0]?.id}`}
                      className="inline-block mt-3 text-amber-700 hover:text-amber-900 underline font-medium"
                    >
                      Go to domain settings to reallocate →
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Budget Input */}
            <div className="mb-6">
              <label htmlFor="urlBudget" className="block text-sm font-medium text-slate-700 mb-2">
                URL Budget for this domain
              </label>
              <div className="flex gap-4 items-center">
                <input
                  type="range"
                  min={minBudget}
                  max={availableBudget}
                  step={1000}
                  value={state.urlBudget}
                  onChange={(e) => setState((s) => ({ ...s, urlBudget: parseInt(e.target.value) }))}
                  className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <input
                  id="urlBudget"
                  type="number"
                  min={minBudget}
                  max={availableBudget}
                  step={1000}
                  value={state.urlBudget}
                  onChange={(e) => {
                    const value = Math.min(Math.max(parseInt(e.target.value) || minBudget, minBudget), availableBudget);
                    setState((s) => ({ ...s, urlBudget: value }));
                  }}
                  className="w-32 px-3 py-2 border rounded-lg text-right font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Minimum: {formatNumber(minBudget)} URLs. You can reallocate this budget later.
              </p>
            </div>

            {/* Info Note */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3 mb-6">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Flexible allocation</p>
                <p>Your URL budget can be redistributed across domains at any time from the domain settings.</p>
              </div>
            </div>

            {state.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {state.error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setState((s) => ({ ...s, step: 'input' }))}
                className="flex-1 py-3 border border-slate-300 rounded-lg font-medium hover:bg-slate-50"
              >
                Back
              </button>
              <button
                onClick={handleBudgetSubmit}
                disabled={state.isLoading || availableBudget < minBudget || state.urlBudget < minBudget || state.urlBudget > availableBudget}
                className="flex-1 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {state.isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : availableBudget < minBudget ? (
                  'Redistribute budget first'
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: CNAME Instructions */}
        {state.step === 'instructions' && state.instructions && (
          <div>
            <h2 className="text-xl font-semibold mb-2">Configure Your DNS</h2>
            <p className="text-slate-600 mb-6">
              Add the following CNAME record to your domain's DNS settings. This tells your domain to
              point to our servers.
            </p>

            <CnameInstructions
              subdomain={state.subdomain}
              targetCname={state.instructions.record.target}
            />

            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> DNS changes can take 5-30 minutes to propagate. If verification
                fails, wait a few minutes and try again.
              </p>
            </div>

            <button
              onClick={() => setState((s) => ({ ...s, step: 'verify' }))}
              className="w-full mt-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 flex items-center justify-center gap-2"
            >
              I've Added the DNS Record
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 3: Verify */}
        {state.step === 'verify' && (
          <div>
            <h2 className="text-xl font-semibold mb-2">Verify Your Domain</h2>
            <p className="text-slate-600 mb-6">
              We'll automatically check your DNS configuration. This usually takes 1-5 minutes.
            </p>

            <div className="p-4 bg-slate-50 rounded-lg mb-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-600">Checking DNS for:</p>
                {state.autoVerifyEnabled && state.verifyStatus !== 'success' && (
                  <span className="flex items-center gap-1.5 text-xs text-blue-600">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Auto-checking every 30s
                  </span>
                )}
              </div>
              <p className="font-mono text-lg">{state.subdomain}</p>
              {state.verifyAttempts > 0 && state.verifyStatus !== 'success' && (
                <p className="text-xs text-slate-400 mt-2">
                  Attempt {state.verifyAttempts} of {MAX_AUTO_VERIFY_ATTEMPTS}
                </p>
              )}
            </div>

            {state.verifyStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">Domain Verified!</p>
                  <p className="text-sm text-green-700">{state.verifyMessage}</p>
                  <p className="text-sm text-green-600 mt-1">Redirecting to domain dashboard...</p>
                </div>
              </div>
            )}

            {state.verifyStatus === 'failed' && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">DNS Not Ready Yet</p>
                  <p className="text-sm text-amber-700">{state.verifyMessage}</p>
                  {state.autoVerifyEnabled ? (
                    <p className="text-sm text-amber-600 mt-1">
                      We'll keep checking automatically. DNS can take 5-30 minutes to propagate.
                    </p>
                  ) : (
                    <p className="text-sm text-amber-600 mt-1">
                      Click "Verify Now" to check again, or enable auto-checking.
                    </p>
                  )}
                </div>
              </div>
            )}

            {state.verifyStatus === 'checking' && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                <div>
                  <p className="font-medium text-blue-800">Checking DNS configuration...</p>
                  <p className="text-sm text-blue-700">Looking up CNAME record for {state.subdomain}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setState((s) => ({ ...s, step: 'instructions' }))}
                className="flex-1 py-3 border border-slate-300 rounded-lg font-medium hover:bg-slate-50"
              >
                Back to Instructions
              </button>
              <button
                onClick={handleVerify}
                disabled={state.verifyStatus === 'checking' || state.verifyStatus === 'success'}
                className="flex-1 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {state.verifyStatus === 'checking' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  'Verify Now'
                )}
              </button>
            </div>

            {/* Auto-verify toggle */}
            {state.verifyStatus !== 'success' && (
              <div className="mt-4 flex items-center justify-center">
                <button
                  onClick={toggleAutoVerify}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  {state.autoVerifyEnabled ? 'Disable' : 'Enable'} auto-checking
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 5: GSC Connection (Optional) */}
        {state.step === 'gsc' && (
          <GscStep
            onConnect={() => {
              // Navigate to GSC connect - will redirect back to settings after
              window.location.href = gscApi.getConnectUrl();
            }}
            onSkip={() => {
              // Navigate to domain detail
              navigate(`/domains/${state.domainId}`);
            }}
          />
        )}
      </div>
    </div>
  );
}
